/**
 * @fileoverview Header badge component showing saved bookings with dropdown.
 */

import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiCalendar,
  FiClock,
  FiUsers,
  FiTrash2,
  FiX,
  FiExternalLink,
} from 'react-icons/fi';
import { useBookingStore, useUpcomingBookingCount, type SavedBooking } from '@/lib/stores/bookingStore';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

/**
 * Bookings badge with dropdown for managing saved bookings.
 */
export function BookingsBadge() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const upcomingCount = useUpcomingBookingCount();
  const { bookings, removeBooking, clearAllBookings } = useBookingStore();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const today = new Date().toISOString().split('T')[0];
  const upcomingBookings = bookings.filter(
    (b) => b.status === 'confirmed' && b.bookingDate >= today
  );
  const pastBookings = bookings.filter(
    (b) => b.status !== 'confirmed' || b.bookingDate < today
  );

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Badge Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'relative flex items-center gap-2 px-3 py-2 rounded-lg transition-colors',
          'hover:bg-accent text-muted-foreground hover:text-foreground',
          isOpen && 'bg-accent text-foreground'
        )}
        aria-label={`${upcomingCount} ${t('myBookings.upcoming').toLowerCase()}`}
        aria-expanded={isOpen}
      >
        <FiCalendar className="h-5 w-5" />
        <span className="hidden sm:inline text-sm font-medium">{t('nav.myBookings')}</span>

        {/* Count Badge */}
        {upcomingCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center text-xs font-bold bg-primary text-primary-foreground rounded-full">
            {upcomingCount > 9 ? '9+' : upcomingCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-background border shadow-xl z-50 overflow-hidden rounded-xl backdrop-blur-xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
              <h3 className="font-semibold">{t('myBookings.title')}</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-lg hover:bg-muted transition-colors"
                aria-label={t('common.close')}
              >
                <FiX className="h-4 w-4" />
              </button>
            </div>

            {/* Content */}
            <div className="max-h-96 overflow-y-auto">
              {bookings.length === 0 ? (
                <div className="p-8 text-center">
                  <FiCalendar className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                  <p className="text-sm text-muted-foreground">{t('myBookings.noBookings')}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('myBookings.noBookingsDesc')}
                  </p>
                </div>
              ) : (
                <>
                  {/* Upcoming Bookings */}
                  {upcomingBookings.length > 0 && (
                    <div>
                      <div className="px-4 py-2 bg-muted/50 border-b">
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          {t('myBookings.upcoming')} ({upcomingBookings.length})
                        </span>
                      </div>
                      {upcomingBookings.map((booking) => (
                        <BookingItem
                          key={booking.id}
                          booking={booking}
                          onRemove={() => removeBooking(booking.id)}
                          onClose={() => setIsOpen(false)}
                        />
                      ))}
                    </div>
                  )}

                  {/* Past Bookings */}
                  {pastBookings.length > 0 && (
                    <div>
                      <div className="px-4 py-2 bg-muted/50 border-b border-t">
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          {t('myBookings.past')} ({pastBookings.length})
                        </span>
                      </div>
                      {pastBookings.slice(0, 3).map((booking) => (
                        <BookingItem
                          key={booking.id}
                          booking={booking}
                          onRemove={() => removeBooking(booking.id)}
                          onClose={() => setIsOpen(false)}
                          isPast
                        />
                      ))}
                      {pastBookings.length > 3 && (
                        <div className="px-4 py-2 text-center">
                          <span className="text-xs text-muted-foreground">
                            {t('myBookings.more', { count: pastBookings.length - 3 })}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            {bookings.length > 0 && (
              <div className="px-4 py-3 border-t bg-muted/30 flex items-center justify-between">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllBookings}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <FiTrash2 className="h-4 w-4 mr-1" />
                  {t('common.clearAll')}
                </Button>
                <Link
                  to="/"
                  onClick={() => setIsOpen(false)}
                  className="text-sm text-primary hover:underline font-medium"
                >
                  {t('nav.newBooking')}
                </Link>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * Individual booking item in the dropdown.
 */
function BookingItem({
  booking,
  onRemove,
  onClose,
  isPast = false,
}: {
  booking: SavedBooking;
  onRemove: () => void;
  onClose: () => void;
  isPast?: boolean;
}) {
  const formattedDate = dayjs(booking.bookingDate).format('ddd, MMM D');
  const formattedTime = dayjs(booking.bookingTime, 'HH:mm').format('h:mm A');
  const manageUrl = `/manage?id=${booking.id}&token=${booking.managementToken}`;

  return (
    <div
      className={cn(
        'px-4 py-3 border-b last:border-b-0 hover:bg-muted/30 transition-colors',
        isPast && 'opacity-60',
        booking.status === 'cancelled' && 'bg-destructive/5'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {/* Date & Time */}
          <div className="flex items-center gap-2 text-sm font-medium">
            <FiCalendar className="h-3.5 w-3.5 text-primary shrink-0" />
            <span>{formattedDate}</span>
            <span className="text-muted-foreground">at</span>
            <FiClock className="h-3.5 w-3.5 text-primary shrink-0" />
            <span>{formattedTime}</span>
          </div>

          {/* Details */}
          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <FiUsers className="h-3 w-3" />
              {booking.numGuests} {booking.numGuests === 1 ? 'guest' : 'guests'}
            </span>
            {booking.tableName && (
              <span className="truncate">{booking.tableName}</span>
            )}
          </div>

          {/* Status */}
          {booking.status === 'cancelled' && (
            <span className="inline-block mt-1 text-xs text-destructive font-medium">
              Cancelled
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <Link
            to={manageUrl}
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
            title="Manage booking"
          >
            <FiExternalLink className="h-4 w-4" />
          </Link>
          <button
            onClick={(e) => {
              e.preventDefault();
              onRemove();
            }}
            className="p-1.5 rounded-lg hover:bg-destructive/10 transition-colors text-muted-foreground hover:text-destructive"
            title="Remove from list"
          >
            <FiTrash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
